// src/components/StripeCardModal.js
import React, { useState, useCallback, useEffect } from 'react'
import { Form, Input, Row, Col, notification, Spin, Checkbox, Button, Modal } from 'antd'
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js'
import client from '../api/client'
const { confirm } = Modal
const stripeInputStyle = {
  style: {
    base: {
      fontSize: '16px',
      color: '#333',
      backgroundColor: '#F6F6F6',
      '::placeholder': { color: '#bbb' },
    },
    invalid: { color: '#f5222d' },
  },
}

export default function StripeCardModal({ onClose }) {
  const [form] = Form.useForm()
  const stripe = useStripe()
  const elements = useElements()
  const [isPrimary, setIsPrimary] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isEditable, setIsEditable] = useState(false)
  const [cardLast4, setCardLast4] = useState(null)

  const fetchCardInfo = useCallback(() => {
    setLoading(true)
    client
      .get(`/api/accounts/stripegetcard`)
      .then((res) => {
        if (res.status === 200) {
          const data = res.data

          form.setFieldsValue({
            name: data.name ?? '',
            addressZip: data.addressZip ?? '',
            isPrimary: data.isPrimary ?? false,
            cardNumber: data.last4 ? `**** **** **** ${data.last4}` : '',
            cardExpiry: data.expMonth && data.expYear ? `${data.expMonth.toString().padStart(2, '0')}/${data.expYear.toString().slice(-2)}` : '',
            cardCvc: data.last4 ? '***' : '',
          })

          setIsPrimary(data.isPrimary ?? false)
          setCardLast4(data.last4 ?? null)
          setIsEditable(true)
        }
      })
      .catch(() => {
        notification.error({ message: 'Картын мэдээлэл авахад алдаа гарлаа' })
      })
      .finally(() => setLoading(false))
  }, [form])

  useEffect(() => {
    fetchCardInfo()
  }, [fetchCardInfo])

  const handleCardRegister = async () => {
    setLoading(true)
    try {
      const values = await form.validateFields()

      if (!stripe || !elements) throw new Error('Stripe init алдаа')

      const cardElement = elements.getElement(CardNumberElement)
      const expiryElement = elements.getElement(CardExpiryElement)
      const cvcElement = elements.getElement(CardCvcElement)

      if (!cardElement || !expiryElement || !cvcElement) {
        notification.error({ message: 'Картын талбарууд дутуу байна!' })
        setLoading(false)
        return
      }

      const result = await stripe.createToken(cardElement, {
        name: values.name,
        address_zip: values.addressZip,
      })

      if (result.error) {
        notification.error({ message: result.error.message })
        setLoading(false)
        return
      }
      const body = {
        token: result.token.id,
        isPrimary: isPrimary ?? false,
      }
      await client.post(`/api/accounts/striperegistercard`, body)
      notification.success({ message: 'Карт амжилттай хадгаллаа' })
      onClose()
    } catch (err) {
      notification.error({ message: 'Карт хадгалах үед алдаа гарлаа' })
    } finally {
      setLoading(false)
    }
  }
  const handleCardDelete = () => {
    confirm({
      title: 'Та энэ картын мэдээллийг засахдаа итгэлтэй байна уу?',
      content: 'Зассан тохиолдолд энэ картын мэдээлэл бүр мөсөн алга болно.',
      okText: 'Тийм, засах',
      okType: 'danger',
      cancelText: 'Болих',
      onOk: async () => {
        setLoading(true)
        try {
          await client.delete('/api/accounts/stripedeletecard')

          notification.success({ message: 'Карт амжилттай заслаа' })

          form.resetFields()
          setCardLast4(null)
          setIsPrimary(false)
          setIsEditable(false)
          fetchCardInfo()
        } catch (err) {
          notification.error({ message: 'Карт засах үед алдаа гарлаа' })
        } finally {
          setLoading(false)
        }
      },
    })
  }
  return (
    <Spin spinning={loading} tip="Уншиж байна...">
      <Form layout="vertical" form={form} onFinish={handleCardRegister}>
        <Row gutter={20}>
          <Col span={12}>
            <Form.Item label="Card holder name" name="name" rules={[{ required: true, message: 'Карт эзэмшигчийн нэр шаардлагатай!' }]}>
              <Input disabled={isEditable} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Card number" name="cardNumber" rules={[{ required: !cardLast4 }]}>
              {isEditable ? <Input disabled /> : <CardNumberElement options={stripeInputStyle} />}
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={12}>
            <Form.Item label="Expiration date" name="cardExpiry" rules={[{ required: !cardLast4 }]}>
              {isEditable ? <Input disabled /> : <CardExpiryElement options={stripeInputStyle} />}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Security code" name="cardCvc" rules={[{ required: !cardLast4 }]}>
              {isEditable ? <Input disabled /> : <CardCvcElement options={stripeInputStyle} />}
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          <Col span={12}>
            <Form.Item label="Zip code" name="addressZip" rules={[{ required: true, message: 'Zip код шаардлагатай!' }]}>
              <Input disabled={isEditable} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Primary card" valuePropName="checked">
              <Checkbox disabled={isEditable} checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)}>
                Primary card
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={20}>
          {isEditable ? (
            <>
              <Col span={12}>
                <Form.Item>
                  <Button block danger onClick={handleCardDelete}>
                    Засах
                  </Button>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item>
                  <Button block type="primary" onClick={handleCardRegister} disabled={isEditable}>
                    Бүртгэх
                  </Button>
                </Form.Item>
              </Col>
            </>
          ) : (
            <Col span={24}>
              <Form.Item>
                <Button block type="primary" htmlType="submit">
                  Бүртгэх
                </Button>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Spin>
  )
}
